import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2, Paperclip, X, Upload, File as FileIcon } from "lucide-react";
import { trackRFQSubmit } from "@/lib/analytics";
import { getAttribution } from "@/lib/attribution";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROCESS_CATEGORIES = [
  "CNC Machining (3/4/5-axis, Swiss, EDM)",
  "Sheet Metal & Fabrication",
  "Forging (open / closed die)",
  "Casting (investment / sand / die)",
  "Injection Molding",
  "Tubings (Inconel / SS / CS)",
  "Coating & Surface Treatment",
  "Electrical Assembly / PCBA",
  "Assembly & QC / NDT",
  "Other",
];

const PRODUCT_CATEGORIES = [
  "Wellhead Components",
  "Flow Iron & Pup Joints",
  "Valves (Gate / Ball / Check)",
  "Fittings (WN / SW / Threaded)",
  "Forgings",
  "Castings",
  "Spools & Manifolds",
  "Pressure Vessels",
  "Heat Exchangers",
  "Structural Steel",
  "Enclosures & Panels",
  "Cables & Harnesses",
  "Switchgear Components",
  "Busbars",
  "Other",
];

const TIMELINES = ["Express (≤48h quote)", "Standard (1 week)", "Planning phase"];

const ACCEPT_EXTS =
  ".step,.stp,.stl,.iges,.igs,.sldprt,.sldasm,.dxf,.dwg,.ipt,.iam,.x_t,.x_b,.prt,.sat,.3mf,.jt,.catpart,.3dxml,.f3d,.obj,.pdf,.xlsx,.xls,.csv,.doc,.docx,.txt,.zip,.rar,.7z,.jpg,.jpeg,.png,.gif,.webp,.heic";

const initial = {
  requirement_type: "process",
  manufacturing_category: "",
  product_category: "",
  other_requirements: "",
  name: "",
  email: "",
  company: "",
  country: "",
  phone: "",
  preferred_contact: "email",
  message: "",
  timeline: "",
};

export default function RFQForm() {
  const [form, setForm] = useState(initial);
  const [files, setFiles] = useState([]); // [{id,name,size,url}]
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [refId, setRefId] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const bytes = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };

  const chosenCategory = form.requirement_type === "process"
    ? form.manufacturing_category
    : form.product_category;

  const uploadFiles = async (fList) => {
    const filesArr = Array.from(fList).slice(0, 10 - files.length);
    if (filesArr.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      filesArr.forEach((f) => fd.append("files", f));
      const res = await axios.post(`${API}/rfq/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFiles((prev) => [...prev, ...res.data.files]);
      toast.success(`Uploaded ${res.data.files.length} file${res.data.files.length === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const onFileInput = (e) => {
    if (e.target.files?.length) uploadFiles(e.target.files);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !chosenCategory || !form.message) {
      toast.error("Please complete name, email, category and brief.");
      return;
    }
    setLoading(true);
    try {
      const attribution = getAttribution() || {};
      let session_id = null;
      try {
        session_id = sessionStorage.getItem("nx_session_id");
      } catch (err) {
        console.warn("rfq: sessionStorage unavailable", err);
      }
      const payload = {
        ...form,
        category: chosenCategory, // legacy field kept for admin dashboard
        attachments: files,
        ...attribution,
        session_id,
      };
      const res = await axios.post(`${API}/rfq`, payload);
      setRefId(res.data.id?.slice(0, 8).toUpperCase() || "NX-OK");
      trackRFQSubmit(chosenCategory);
      setSuccess(true);
      setForm(initial);
      setFiles([]);
      toast.success("RFQ received — we'll respond within 48 hours.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="border border-nx-navy bg-white p-8 md:p-12"
        data-testid="rfq-success"
      >
        <p className="font-mono text-[0.65rem] uppercase tracking-[0.24em] text-nx-steel2 mb-6">
          → Ref {refId} · Received
        </p>
        <h3 className="font-cabinet font-black text-4xl md:text-5xl tracking-tight text-nx-ink leading-[0.95]">
          Brief received.<br />
          <span className="font-serif italic font-normal text-nx-navy2">An engineer is on it.</span>
        </h3>
        <p className="mt-6 max-w-lg text-nx-navy2 leading-relaxed">
          Your RFQ has been routed to the Nexoriq sourcing desk. Expect a benchmarked quote within 48 hours. For urgent scope, call{" "}
          <a href="tel:+971508003202" className="underline underline-offset-4">+971 50 800 3202</a>.
        </p>
        <button onClick={() => setSuccess(false)} data-testid="rfq-new" className="nx-btn mt-8">
          Submit another brief <ArrowUpRight size={14} />
        </button>
      </motion.div>
    );
  }

  const inputCls =
    "w-full bg-transparent border-b border-nx-navy/60 focus:border-nx-navy py-3 outline-none font-ibm text-nx-navy placeholder:text-nx-steel2 transition-colors";

  return (
    <form onSubmit={submit} data-testid="rfq-form" className="space-y-10">
      {/* Section 01 — Requirement type */}
      <Section num="01" title="What do you need?">
        <div className="flex flex-wrap gap-3" data-testid="rfq-requirement-type">
          {[
            ["process", "Process (Manufacturing service)"],
            ["product", "Product (Finished part)"],
          ].map(([val, label]) => (
            <label
              key={val}
              className={
                "cursor-pointer border px-4 py-3 flex items-center gap-3 transition-colors " +
                (form.requirement_type === val
                  ? "bg-nx-navy text-nx-bg border-nx-navy"
                  : "bg-transparent text-nx-navy border-nx-navy/40 hover:border-nx-navy")
              }
            >
              <input
                type="radio"
                name="requirement_type"
                value={val}
                checked={form.requirement_type === val}
                onChange={update("requirement_type")}
                className="hidden"
              />
              <span className="font-mono text-[0.7rem] uppercase tracking-[0.16em]">{label}</span>
            </label>
          ))}
        </div>
      </Section>

      {/* Section 02 — Category */}
      <Section
        num="02"
        title={form.requirement_type === "process" ? "Manufacturing category" : "Product category"}
      >
        {form.requirement_type === "process" ? (
          <select
            required
            value={form.manufacturing_category}
            onChange={update("manufacturing_category")}
            data-testid="rfq-manufacturing-category"
            className={inputCls + " appearance-none cursor-pointer"}
          >
            <option value="">Select a process —</option>
            {PROCESS_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        ) : (
          <select
            required
            value={form.product_category}
            onChange={update("product_category")}
            data-testid="rfq-product-category"
            className={inputCls + " appearance-none cursor-pointer"}
          >
            <option value="">Select a product —</option>
            {PRODUCT_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        )}
      </Section>

      {/* Section 03 — Other requirements */}
      <Section num="03" title="Any other requirements?">
        <input
          type="text"
          value={form.other_requirements}
          onChange={update("other_requirements")}
          data-testid="rfq-other-requirements"
          className={inputCls}
          placeholder="Materials, standards (API 6A, ASME, IEC), certifications, coatings…"
        />
      </Section>

      {/* Section 04 — Contact */}
      <Section num="04" title="Contact details">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          <Field label="Name" required>
            <input type="text" required value={form.name} onChange={update("name")} data-testid="rfq-name" className={inputCls} placeholder="Jane Cooper" />
          </Field>
          <Field label="Email" required>
            <input type="email" required value={form.email} onChange={update("email")} data-testid="rfq-email" className={inputCls} placeholder="jane@company.com" />
          </Field>
          <Field label="Company">
            <input type="text" value={form.company} onChange={update("company")} data-testid="rfq-company" className={inputCls} placeholder="Company Ltd." />
          </Field>
          <Field label="Phone">
            <input type="tel" value={form.phone} onChange={update("phone")} data-testid="rfq-phone" className={inputCls} placeholder="+44 …" />
          </Field>
          <Field label="Country">
            <input type="text" value={form.country} onChange={update("country")} data-testid="rfq-country" className={inputCls} placeholder="United Kingdom" />
          </Field>
        </div>

        <div className="mt-6">
          <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-nx-steel2 mb-3">
            Preferred contact method
          </p>
          <div className="flex flex-wrap gap-3" data-testid="rfq-preferred-contact">
            {[
              ["email", "Email"],
              ["phone", "Phone"],
              ["whatsapp", "WhatsApp"],
            ].map(([val, label]) => (
              <label
                key={val}
                className={
                  "cursor-pointer border px-3 py-2 flex items-center gap-2 transition-colors " +
                  (form.preferred_contact === val
                    ? "bg-nx-navy text-nx-bg border-nx-navy"
                    : "bg-transparent text-nx-navy border-nx-navy/40 hover:border-nx-navy")
                }
              >
                <input
                  type="radio"
                  name="preferred_contact"
                  value={val}
                  checked={form.preferred_contact === val}
                  onChange={update("preferred_contact")}
                  className="hidden"
                />
                <span className="font-mono text-[0.68rem] uppercase tracking-[0.14em]">{label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <Field label="Timeline">
            <select value={form.timeline} onChange={update("timeline")} data-testid="rfq-timeline" className={inputCls + " appearance-none cursor-pointer"}>
              <option value="">Select —</option>
              {TIMELINES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>
      </Section>

      {/* Section 05 — Brief */}
      <Section num="05" title="Questions & comments">
        <textarea
          required
          rows={5}
          value={form.message}
          onChange={update("message")}
          data-testid="rfq-message"
          className={inputCls + " resize-none"}
          placeholder="Anything else the sourcing desk should know — drawings referenced below, tolerances, target market, prior suppliers, urgency."
        />
      </Section>

      {/* Section 06 — Attachments */}
      <Section num="06" title="Upload parts list / drawings">
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid="rfq-dropzone"
          className={
            "border-2 border-dashed p-6 md:p-8 flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors " +
            (dragOver
              ? "border-nx-accent bg-nx-accent/5"
              : "border-nx-navy/40 hover:border-nx-navy")
          }
        >
          <Upload size={22} className="text-nx-navy2" />
          <div className="text-center">
            <div className="font-cabinet font-black text-nx-ink text-lg tracking-tight">
              Drop parts list or drawings here
            </div>
            <div className="mt-1 font-mono text-[0.62rem] uppercase tracking-[0.14em] text-nx-steel2">
              or click to browse · up to 10 files · 25 MB each
            </div>
            <div className="mt-2 text-xs text-nx-steel2 max-w-md">
              STEP · STP · SLDPRT · STL · DXF · DWG · IPT · X_T · X_B · 3DXML · CATPART · PRT · SAT · 3MF · JT · PDF · XLSX · CSV · DOC · ZIP · JPG · PNG
            </div>
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT_EXTS}
            onChange={onFileInput}
            data-testid="rfq-file-input"
            className="hidden"
          />
        </div>

        {uploading && (
          <div className="mt-3 flex items-center gap-2 text-sm text-nx-navy2">
            <Loader2 size={14} className="animate-spin" /> Uploading…
          </div>
        )}

        {files.length > 0 && (
          <ul className="mt-4 space-y-2" data-testid="rfq-file-list">
            {files.map((f) => (
              <li
                key={f.id}
                className="flex items-center justify-between gap-3 px-3 py-2 border border-nx-navy/25 bg-nx-bg2"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileIcon size={14} className="text-nx-navy2 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-nx-navy truncate text-sm">{f.name}</div>
                    <div className="font-mono text-[0.6rem] uppercase tracking-[0.14em] text-nx-steel2">{bytes(f.size)}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(f.id)}
                  data-testid={`rfq-file-remove-${f.id}`}
                  className="text-nx-steel2 hover:text-nx-navy"
                  aria-label="Remove file"
                >
                  <X size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-nx-navy/20">
        <p className="text-xs text-nx-steel2 max-w-md leading-relaxed">
          By submitting, you agree to be contacted by the Nexoriq sourcing desk regarding your brief. No spam. No sharing with third parties.
        </p>
        <button type="submit" disabled={loading || uploading} data-testid="rfq-submit" className="nx-btn disabled:opacity-60 disabled:cursor-not-allowed">
          {loading ? (<><Loader2 size={14} className="animate-spin" /> Submitting</>) : (<>Submit RFQ <ArrowUpRight size={14} /></>)}
        </button>
      </div>
    </form>
  );
}

function Section({ num, title, children }) {
  return (
    <section className="pb-4">
      <div className="flex items-baseline gap-3 mb-4">
        <span className="font-mono text-[0.62rem] uppercase tracking-[0.24em] text-nx-steel2">
          [{num}]
        </span>
        <h3 className="font-cabinet font-black text-nx-ink text-xl md:text-2xl tracking-tight">
          {title}
        </h3>
      </div>
      {children}
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="flex items-center mb-2 font-mono text-[0.6rem] uppercase tracking-[0.2em] text-nx-steel2">
        {label}
        {required && <span className="text-nx-navy ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
